import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useLocale } from "./useLocale";
import { ClinicConfig } from "../graphql/clinic/clinicTypes";

export interface LocaleContext {
  locale: string;
  changeLocale: Dispatch<string>;
  isLoadingTranslations: boolean;
  translations: Record<string, string>;
  setClinicConfig: Dispatch<ClinicConfig>;
  setConversationOverrides: Dispatch<Record<string, string>>;
}

const localeContext = createContext<LocaleContext>(null);

export const useLocaleContext = () => useContext(localeContext);

export type LocaleContextProviderProps = {
  baseLocale: string;
  children: React.ReactNode;
  partnerId: string;
};

export const LocaleContextProvider = ({
  baseLocale,
  children,
  ...props
}: LocaleContextProviderProps) => {
  const { changeLocale: updateLocaleState, ...useLocaleHook } = useLocale(
    baseLocale,
    props.partnerId,
  );

  const changeLocale = useCallback((newLocale: string) => {
    // console.log("Updating to new locale", newLocale);
    updateLocaleState(newLocale);
  }, []);

  const useLocaleValue = useMemo(() => ({ ...useLocaleHook, changeLocale }), [
    useLocaleHook,
  ]);

  return (
    <localeContext.Provider value={useLocaleValue}>
      {children}
    </localeContext.Provider>
  );
};
